import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { FileText, TrendingUp, ChevronRight, Sparkles } from 'lucide-react';

const fmtEur = (n) => {
  const num = Number(n) || 0;
  if (num === 0) return '€ 0';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(num);
};

/**
 * ProposalsPipelineCard — 2026-07-03 (richiesta utente)
 *
 * Mostra il potenziale fatturato dei lead in stage `proposta_inviata` e
 * (opzionale) `chiuso`. Aiuta il commerciale/admin a capire quanto è "in
 * campo" prima della chiusura effettiva.
 *
 * Fonte importo (backend `_amount_of`):
 *   1. custom_fields.proposal_amount_eur
 *   2. custom_fields.closed_amount_eur
 *   3. bracket max del budget_bracket (fallback qualifica)
 */
export default function ProposalsPipelineCard({ startDate, endDate, agentId, refreshTick = 0 }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (agentId) params.append('agent_id', agentId);
    params.append('include_closed', 'true');

    api.get(`/kpis/proposals?${params.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || t('common.loadError', 'Errore caricamento'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startDate, endDate, agentId, refreshTick]);

  if (loading) {
    return (
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50/50 to-white" data-testid="proposals-card-loading">
        <CardContent className="p-4">
          <div className="text-xs text-muted-foreground">{t('proposalsCard.loading', 'Caricamento potenziale proposte…')}</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-border" data-testid="proposals-card-error">
        <CardContent className="p-4 text-xs text-red-600">{t('proposalsCard.error', 'Errore proposte:')} {error}</CardContent>
      </Card>
    );
  }

  const d = data || {};
  const proposalCount = d.proposta_inviata_count || 0;
  const proposalSum = d.proposta_inviata_potential_eur || 0;
  const closedCount = d.chiuso_count || 0;
  const closedSum = d.chiuso_amount_eur || 0;
  const total = d.total_potential_eur || 0;

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50/60 to-white" data-testid="proposals-pipeline-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-700" />
            <div>
              <div className="text-[11px] uppercase tracking-wide text-blue-800 font-semibold">
                {t('proposalsCard.title', 'Potenziale fatturato · Proposte')}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {t('proposalsCard.subtitle', 'Somma dai lead in “Proposta inviata” + Chiusi del periodo selezionato')}
              </div>
            </div>
          </div>
          <Link to="/app/pipeline" className="text-xs text-blue-700 hover:opacity-80 inline-flex items-center" data-testid="proposals-goto-pipeline">
            {t('proposalsCard.goPipeline', 'Vai al Pipeline')} <ChevronRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Totale principale */}
          <div className="md:col-span-1 rounded-lg border-2 border-blue-300 bg-white p-3" data-testid="proposals-total-tile">
            <div className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold">{t('proposalsCard.periodTotal', 'Totale periodo')}</div>
            <div className="font-serif text-3xl mt-1 text-blue-900" data-testid="proposals-total-eur">
              {fmtEur(total)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {proposalCount + closedCount} lead ({proposalCount} proposte + {closedCount} chiusi)
            </div>
          </div>

          {/* Solo Proposte */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3" data-testid="proposals-sent-tile">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-blue-800 font-semibold">
              <TrendingUp className="w-3 h-3" />
              Proposte inviate
            </div>
            <div className="font-serif text-2xl mt-1 text-blue-800" data-testid="proposals-sent-amount">
              {fmtEur(proposalSum)}
            </div>
            <div className="text-[11px] text-muted-foreground">{proposalCount} lead in pipeline</div>
          </div>

          {/* Solo Chiusi */}
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="proposals-closed-tile">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-emerald-800 font-semibold">
              <Sparkles className="w-3 h-3" />
              Chiusi confermati
            </div>
            <div className="font-serif text-2xl mt-1 text-emerald-800" data-testid="proposals-closed-amount">
              {fmtEur(closedSum)}
            </div>
            <div className="text-[11px] text-muted-foreground">{closedCount} chiusi nel periodo</div>
          </div>
        </div>

        {/* Top 5 proposte */}
        {Array.isArray(d.items) && d.items.length > 0 && (
          <div className="mt-3 pt-3 border-t border-blue-200/70">
            <div className="text-[10px] uppercase tracking-wide text-blue-800 font-semibold mb-1.5">
              Top proposte per importo
            </div>
            <div className="space-y-1">
              {d.items.slice(0, 5).map((it) => (
                <Link
                  key={it.id}
                  to={`/app/leads/${it.id}`}
                  className="flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-blue-50 transition-colors"
                  data-testid={`proposals-item-${it.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${it.stage === 'chiuso' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                    <span className="truncate">{it.name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      · {it.source === 'proposal' ? 'valore proposta' : it.source === 'closed' ? 'valore chiuso' : 'fascia budget'}
                    </span>
                  </div>
                  <span className="font-mono font-semibold text-foreground tabular-nums shrink-0 ml-2">
                    {fmtEur(it.amount_eur)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {proposalCount + closedCount === 0 && (
          <div className="mt-3 text-xs text-muted-foreground italic text-center py-3 border-t border-blue-200/70">
            {t('proposalsCard.empty', 'Nessuna proposta inviata o chiusa nel periodo selezionato.')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
